import React, { useState } from 'react';
import { Sparkles, ArrowRight, AlertTriangle, CheckCircle2, TrendingUp, ShieldAlert, Activity, FileText, Download, Wrench, X, Check, Loader2, Copy, BookmarkX, EyeOff, ClipboardList, ChevronDown, ChevronUp, Bot, Shield } from 'lucide-react';
import type { Environment } from './RecentScans';
interface CriticalNextStepProps {
  environments: Environment[];
  priorityClient?: Environment;
  problemClients?: Environment[];
  activeClientIndex?: number;
  onClientIndexChange?: (index: number) => void;
  onManageEnvironment?: (env: Environment) => void;
  onClientChange?: () => void;
  onViewClientVulns?: (env: Environment) => void;
  onViewReport?: (env: Environment, sections: string[]) => void;
}
interface RemediationFinding {
  id: string;
  severity: 'HIGH' | 'MEDIUM' | 'LOW';
  title: string;
  resourceType: string;
  resourceName: string;
  affected: number;
  description: string;
  affectedResource: string;
  recommendation: string;
  suggestedPolicy?: string;
}
export function CriticalNextStep({
  environments = [],
  priorityClient: externalPriorityClient,
  problemClients = [],
  activeClientIndex = 0,
  onClientIndexChange,
  onManageEnvironment,
  onClientChange,
  onViewClientVulns,
  onViewReport
}: CriticalNextStepProps) {
  const [showReportPanel, setShowReportPanel] = useState(false);
  const [showRemediationAssistant, setShowRemediationAssistant] = useState(false);
  const [expandedFinding, setExpandedFinding] = useState<string | null>(null);
  const [copiedResource, setCopiedResource] = useState<string | null>(null);
  const [dismissedFindings, setDismissedFindings] = useState<string[]>([]);
  const [reportGenerating, setReportGenerating] = useState(false);
  const [reportReady, setReportReady] = useState(false);
  const [selectedReportSections, setSelectedReportSections] = useState<string[]>(['executive-summary', 'over-privileged', 'compliance', 'recommendations']);
  const [lastClientId, setLastClientId] = useState<string | undefined>(externalPriorityClient?.id);
  if (externalPriorityClient?.id !== lastClientId) {
    setLastClientId(externalPriorityClient?.id);
    setShowReportPanel(false);
    setShowRemediationAssistant(false);
    setReportReady(false);
    setReportGenerating(false);
    setExpandedFinding(null);
    setDismissedFindings([]);
  }
  const reportSections = [{
    id: 'executive-summary',
    label: 'Executive Summary'
  }, {
    id: 'over-privileged',
    label: 'Over-Privileged Identities'
  }, {
    id: 'compliance',
    label: 'Compliance Posture'
  }, {
    id: 'recommendations',
    label: 'Remediation Steps'
  }, {
    id: 'identity-audit',
    label: 'Full Identity Audit'
  }, {
    id: 'policy-review',
    label: 'Policy Review'
  }];
  const toggleSection = (id: string) => {
    setSelectedReportSections(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };
  const handleGenerateReport = () => {
    setReportGenerating(true);
    setReportReady(false);
    setTimeout(() => {
      setReportGenerating(false);
      setReportReady(true);
    }, 2200);
  };
  const handleCopyResource = (resource: string, id: string) => {
    navigator.clipboard?.writeText(resource);
    setCopiedResource(id);
    setTimeout(() => setCopiedResource(null), 2000);
  };
  const criticalCount = environments.filter(e => e.status === 'critical').length;
  const warningCount = environments.filter(e => e.status === 'warning').length;
  const avgCompliance = Math.round(environments.reduce((acc, curr) => acc + curr.complianceScore, 0) / (environments.length || 1));
  const totalIdentities = environments.reduce((acc, curr) => acc + curr.identities, 0);
  const priorityClient = externalPriorityClient || [...environments].sort((a, b) => a.complianceScore - b.complianceScore)[0];
  const remediationFindings: RemediationFinding[] = priorityClient ? priorityClient.status === 'critical' ? [{
    id: 'REM-001',
    severity: 'HIGH',
    title: 'Service Account with Owner Role',
    resourceType: 'Service Principal',
    resourceName: `sp-${priorityClient.clientName.toLowerCase().replace(/\s+/g, '-')}-deploy`,
    affected: 3,
    description: `Service principal has Owner role assigned at subscription scope. This grants full control over all resources including the ability to assign roles to other identities. This violates least-privilege principles.`,
    affectedResource: `arn:iam::${priorityClient.id}:role/sp-deploy-owner`,
    recommendation: 'Scope down to Contributor on specific resource groups. Remove role assignment capability by switching from Owner to a custom role.',
    suggestedPolicy: `{
  "roleName": "Custom-Deploy-Contributor",
  "scope": "/subscriptions/.../resourceGroups/prod",
  "permissions": {
    "actions": ["Microsoft.Resources/*", "Microsoft.Compute/*"],
    "notActions": ["*/roleAssignments/*"]
  }
}`
  }, {
    id: 'REM-002',
    severity: 'HIGH',
    title: 'Stale Admin Account — No MFA',
    resourceType: 'User Account',
    resourceName: `admin-legacy@${priorityClient.clientName.toLowerCase().replace(/\s+/g, '')}.com`,
    affected: 1,
    description: `Admin account has not authenticated in 90+ days but retains Global Administrator role. MFA is not enforced via Conditional Access policy. High risk for credential compromise.`,
    affectedResource: `user:admin-legacy@${priorityClient.clientName.toLowerCase().replace(/\s+/g, '')}.com`,
    recommendation: 'Disable account immediately. If still needed, enforce MFA via Conditional Access and downgrade to a scoped admin role.'
  }, {
    id: 'REM-003',
    severity: 'MEDIUM',
    title: 'Wildcard Permissions on IAM Policy',
    resourceType: 'IAM Policy',
    resourceName: `policy-${priorityClient.clientName.toLowerCase().replace(/\s+/g, '-')}-legacy`,
    affected: 12,
    description: `IAM policy uses wildcard (*) actions on multiple resource types. This grants broad access that likely exceeds operational requirements.`,
    affectedResource: `arn:iam::${priorityClient.id}:policy/legacy-admin-access`,
    recommendation: 'Analyze CloudTrail/Activity logs to identify actually-used permissions. Generate a least-privilege policy based on 90-day usage data.',
    suggestedPolicy: `{
  "effect": "Allow",
  "actions": [
    "s3:GetObject", "s3:PutObject",
    "ec2:DescribeInstances", "ec2:StartInstances",
    "logs:CreateLogGroup", "logs:PutLogEvents"
  ],
  "resources": ["arn:aws:s3:::prod-bucket/*"]
}`
  }, {
    id: 'REM-004',
    severity: 'MEDIUM',
    title: 'Unused API Keys — Active 180+ Days',
    resourceType: 'Access Key',
    resourceName: `AKIA...X7Q9 (${priorityClient.clientName})`,
    affected: 2,
    description: `Access keys have been active for 180+ days with no recent API calls. Unused credentials increase the attack surface and risk of credential leakage.`,
    affectedResource: `arn:iam::${priorityClient.id}:user/svc-pipeline/accesskey/AKIA...X7Q9`,
    recommendation: 'Deactivate unused keys immediately. Implement automated key rotation policy with a 90-day maximum lifetime.'
  }, {
    id: 'REM-005',
    severity: 'HIGH',
    title: 'Unregistered AI Agent with Admin API Access',
    resourceType: 'Service Account (Shadow AI)',
    resourceName: `svc-${priorityClient.clientName.toLowerCase().replace(/\s+/g, '-')}-llm-agent-prod`,
    affected: 8,
    description: `An autonomous AI agent was discovered operating in the production environment with admin-level API credentials. The service account was provisioned 62 days ago outside the standard change management process and is not tracked in the service catalog. The agent has made 14,200+ API calls including write operations to production databases, secret store reads, and IAM role modifications.`,
    affectedResource: `arn:iam::${priorityClient.id}:service-account/svc-llm-agent-prod`,
    recommendation: 'Immediately rotate the API credentials and restrict the service account to read-only access. Conduct a full audit of all actions performed by this identity. Register the agent in the service catalog with a designated owner and enforce human-in-the-loop approval for write operations.',
    suggestedPolicy: `{
  "policyName": "AI-Agent-Restricted-ReadOnly",
  "permissions": {
    "actions": [
      "s3:GetObject", "s3:ListBucket",
      "ec2:Describe*", "logs:GetLogEvents"
    ],
    "notActions": [
      "iam:*", "s3:PutObject", "s3:DeleteObject",
      "ec2:RunInstances", "ec2:TerminateInstances",
      "secretsmanager:GetSecretValue"
    ]
  },
  "conditions": {
    "requireApprovalGate": true,
    "maxSessionDuration": "1h",
    "mandatoryTags": ["ai-agent", "owner", "purpose"]
  }
}`
  }] : [{
    id: 'REM-W01',
    severity: 'MEDIUM',
    title: 'Overly Broad S3 Bucket Policy',
    resourceType: 'S3 Bucket Policy',
    resourceName: `${priorityClient.clientName.toLowerCase().replace(/\s+/g, '-')}-data-lake`,
    affected: 5,
    description: `S3 bucket policy grants s3:* to a wide set of IAM principals. Multiple service roles have full read/write access when only read is required for most.`,
    affectedResource: `arn:aws:s3:::${priorityClient.clientName.toLowerCase().replace(/\s+/g, '-')}-data-lake`,
    recommendation: 'Restrict bucket policy to specific actions (s3:GetObject, s3:ListBucket) per principal. Use separate policies for write access where needed.',
    suggestedPolicy: `{
  "effect": "Allow",
  "principal": { "AWS": "arn:aws:iam::role/data-reader" },
  "actions": ["s3:GetObject", "s3:ListBucket"],
  "resources": ["arn:aws:s3:::data-lake/*"]
}`
  }, {
    id: 'REM-W02',
    severity: 'MEDIUM',
    title: 'Cross-Account Role Trust Too Permissive',
    resourceType: 'IAM Role',
    resourceName: `role-${priorityClient.clientName.toLowerCase().replace(/\s+/g, '-')}-cross-acct`,
    affected: 3,
    description: `Cross-account assume-role trust policy allows any principal from the trusted account to assume this role. Should be scoped to specific roles or users.`,
    affectedResource: `arn:iam::${priorityClient.id}:role/cross-account-admin`,
    recommendation: 'Update trust policy to specify exact principal ARNs instead of the root account. Add external ID condition for additional security.'
  }, {
    id: 'REM-W03',
    severity: 'LOW',
    title: 'CloudWatch Log Group Retention Not Set',
    resourceType: 'CloudWatch',
    resourceName: `/aws/lambda/${priorityClient.clientName.toLowerCase().replace(/\s+/g, '-')}-api`,
    affected: 8,
    description: `CloudWatch log groups have no retention policy set, defaulting to indefinite retention. This increases storage costs and may conflict with data retention policies.`,
    affectedResource: `arn:aws:logs:us-east-1:${priorityClient.id}:log-group:/aws/lambda/api`,
    recommendation: 'Set retention policy to 90 days for operational logs and 365 days for audit logs. Archive older logs to S3 Glacier if needed for compliance.'
  }] : [];
  const activeFindings = remediationFindings.filter(f => !dismissedFindings.includes(f.id));
  const highCount = activeFindings.filter(f => f.severity === 'HIGH').length;
  const mediumCount = activeFindings.filter(f => f.severity === 'MEDIUM').length;
  return <div className="relative overflow-hidden rounded-xl bg-astro-card border border-astro-border h-full flex flex-col" data-id="element-204">
      {/* AI Header */}
      <div className="border-b border-astro-border bg-gradient-to-r from-astro-green/5 to-transparent" data-id="element-205">
        <div className="p-6 pb-4" data-id="element-206">
          <div className="flex items-center gap-2 mb-2" data-id="element-207">
            <Sparkles size={16} className="text-astro-green animate-pulse" data-id="element-208" />
            <span className="text-xs font-bold text-astro-green tracking-wider uppercase" data-id="element-209">
              AI Portfolio Briefing
            </span>
          </div>
          <h2 className="text-xl font-bold text-white" data-id="element-210">
            {criticalCount > 0 ? `${criticalCount} clients require immediate attention` : 'All systems operational across portfolio'}
          </h2>
          <p className="text-astro-text-secondary text-sm mt-1" data-id="element-211">
            {criticalCount > 0 ? `Analysis detected critical security gaps in ${priorityClient?.clientName} and ${criticalCount - 1} other environments.` : 'Routine scanning complete. No critical vulnerabilities detected in the last 24 hours.'}
          </p>
        </div>

        {/* Client Issue Tabs */}
        {problemClients.length > 1 && <div className="flex items-end px-6 gap-0 -mb-px" data-id="element-212">
            {problemClients.map((client, i) => {
          const isActive = i === activeClientIndex;
          const isCritical = client.status === 'critical';
          return <button key={client.id} onClick={() => onClientIndexChange?.(i)} className={`relative flex items-center gap-2 px-4 py-2.5 text-xs font-medium transition-all border-b-2 ${isActive ? isCritical ? 'text-astro-danger border-astro-danger bg-astro-danger/5' : 'text-astro-green border-astro-green bg-astro-green/5' : 'text-astro-text-secondary border-transparent hover:text-white hover:bg-white/[0.02]'}`} data-id="element-213">
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isCritical ? 'bg-astro-danger' : 'bg-astro-text-secondary'} ${isActive ? 'animate-pulse' : 'opacity-40'}`} data-id="element-214" />

                  {client.clientName}
                  <span className={`text-[10px] font-mono ${isActive ? 'opacity-70' : 'opacity-40'}`} data-id="element-215">
                    {client.complianceScore}%
                  </span>
                </button>;
        })}
          </div>}
      </div>

      <div className="p-6 flex-1 flex flex-col gap-6 overflow-y-auto" data-id="element-216">
        {/* Key Metrics - Per Client */}
        <div className="grid grid-cols-3 gap-4" data-id="element-217">
          <div className="bg-[#0a0e14] border border-astro-border rounded-lg p-3" data-id="element-218">
            <div className="flex items-center gap-2 text-astro-text-secondary text-[10px] uppercase tracking-wider mb-1" data-id="element-219">
              <Activity size={12} data-id="element-220" /> Compliance
            </div>
            <div className={`text-2xl font-mono font-bold ${priorityClient && priorityClient.complianceScore < 70 ? 'text-astro-danger' : priorityClient && priorityClient.complianceScore < 85 ? 'text-white' : 'text-astro-green'}`} data-id="element-221">
              {priorityClient ? priorityClient.complianceScore : avgCompliance}%
            </div>
            <div className={`text-[10px] flex items-center gap-1 mt-1 ${priorityClient && priorityClient.complianceScore < 80 ? 'text-astro-danger' : 'text-astro-green'}`} data-id="element-222">
              <TrendingUp size={10} data-id="element-223" />
              {priorityClient ? priorityClient.complianceScore >= 90 ? 'On target' : priorityClient.complianceScore >= 80 ? 'Needs review' : 'Below threshold' : `Avg: ${avgCompliance}%`}
            </div>
          </div>

          <div className="bg-[#0a0e14] border border-astro-border rounded-lg p-3" data-id="element-224">
            <div className="flex items-center gap-2 text-astro-text-secondary text-[10px] uppercase tracking-wider mb-1" data-id="element-225">
              <ShieldAlert size={12} data-id="element-226" /> Over-Privileged
            </div>
            <div className={`text-2xl font-mono font-bold ${priorityClient && priorityClient.overPrivileged > 50 ? 'text-astro-danger' : priorityClient && priorityClient.overPrivileged > 20 ? 'text-white' : 'text-astro-green'}`} data-id="element-227">
              {priorityClient ? priorityClient.overPrivileged : criticalCount}
            </div>
            <div className="text-[10px] text-astro-text-secondary mt-1" data-id="element-228">
              {priorityClient ? `${priorityClient.type} · ${priorityClient.status}` : `${warningCount} warnings`}
            </div>
          </div>

          <div className="bg-[#0a0e14] border border-astro-border rounded-lg p-3" data-id="element-229">
            <div className="flex items-center gap-2 text-astro-text-secondary text-[10px] uppercase tracking-wider mb-1" data-id="element-230">
              <CheckCircle2 size={12} data-id="element-231" /> Identities
            </div>
            <div className="text-2xl font-mono font-bold text-white" data-id="element-232">
              {priorityClient ? priorityClient.identities >= 1000 ? `${(priorityClient.identities / 1000).toFixed(1)}k` : priorityClient.identities : `${(totalIdentities / 1000).toFixed(1)}k`}
            </div>
            <div className="text-[10px] text-astro-text-secondary mt-1" data-id="element-233">
              {priorityClient ? `Synced ${priorityClient.lastSync}` : `Across ${environments.length} envs`}
            </div>
          </div>
        </div>

        {/* Priority Action Item */}
        {priorityClient && !showRemediationAssistant && <div className="bg-astro-danger/5 border border-astro-danger/20 rounded-lg p-4" data-id="element-234">
            <div className="flex items-start gap-4" data-id="element-235">
              <div className={`p-2 rounded-lg shrink-0 ${priorityClient.status === 'critical' ? 'bg-astro-danger/10 text-astro-danger' : 'bg-white/5 text-astro-text-secondary'}`} data-id="element-236">
                <AlertTriangle size={20} data-id="element-237" />
              </div>
              <div className="flex-1" data-id="element-238">
                <div className="flex items-center justify-between mb-1" data-id="element-239">
                  <div className="flex items-center gap-2" data-id="element-240">
                    <h3 className="text-sm font-bold text-white" data-id="element-241">
                      {priorityClient.status === 'critical' ? 'Critical' : 'Warning'}
                      : {priorityClient.clientName}
                    </h3>
                    <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${priorityClient.status === 'critical' ? 'bg-astro-danger/10 text-astro-danger border-astro-danger/20' : 'bg-white/5 text-astro-text-secondary border-white/10'}`} data-id="element-242">
                      {priorityClient.type}
                    </span>
                  </div>
                  <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${priorityClient.status === 'critical' ? 'text-astro-danger bg-astro-danger/10 border-astro-danger/20' : 'text-astro-text-secondary bg-white/5 border-white/10'}`} data-id="element-243">
                    Score: {priorityClient.complianceScore}%
                  </span>
                </div>
                <p className="text-xs text-astro-text-secondary mb-3" data-id="element-244">
                  {priorityClient.overPrivileged} over-privileged identities
                  detected.{' '}
                  {priorityClient.status === 'critical' ? 'Immediate remediation recommended.' : 'Review and remediation advised.'}
                </p>
                <div className="flex flex-wrap items-center gap-2" data-id="element-245">
                  <button onClick={() => {
                setShowReportPanel(!showReportPanel);
                setShowRemediationAssistant(false);
                setReportReady(false);
                setReportGenerating(false);
              }} className={`text-xs font-bold px-3 py-1.5 rounded transition-colors flex items-center gap-1.5 border ${showReportPanel ? 'text-astro-green bg-astro-green/10 border-astro-green/30' : 'text-white bg-[#0a0e14] border-astro-border hover:border-astro-green/30 hover:text-astro-green'}`} data-id="element-246">
                    <FileText size={12} data-id="element-247" />
                    Generate Report
                  </button>
                  <button onClick={() => {
                if (onViewClientVulns && priorityClient) {
                  onViewClientVulns(priorityClient);
                }
              }} className="text-xs font-bold text-white bg-astro-danger hover:bg-astro-danger/90 px-3 py-1.5 rounded transition-colors shadow-[0_0_10px_rgba(239,68,68,0.2)] flex items-center gap-1.5" data-id="element-248">
                    <Wrench size={12} data-id="element-249" />
                    Remediate Now <ArrowRight size={12} data-id="element-250" />
                  </button>
                </div>
              </div>
            </div>

            {/* Report Generation Panel */}
            {showReportPanel && <div className="mt-4 pt-4 border-t border-astro-danger/10" data-id="element-251">
                <div className="bg-[#0a0e14] border border-astro-border rounded-lg p-4" data-id="element-252">
                  <div className="flex items-center justify-between mb-3" data-id="element-253">
                    <div className="flex items-center gap-2" data-id="element-254">
                      <Sparkles size={14} className="text-astro-green" data-id="element-255" />
                      <h4 className="text-sm font-bold text-white" data-id="element-256">
                        AI Report Builder
                      </h4>
                    </div>
                    <button onClick={() => setShowReportPanel(false)} className="text-astro-text-secondary hover:text-white transition-colors" data-id="element-257">
                      <X size={14} data-id="element-258" />
                    </button>
                  </div>
                  <p className="text-[11px] text-astro-text-secondary mb-3" data-id="element-259">
                    Generate a client-ready remediation report for{' '}
                    <span className="text-white font-medium" data-id="element-260">
                      {priorityClient.clientName}
                    </span>
                    . Select sections to include:
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4" data-id="element-261">
                    {reportSections.map(section => {
                const isSelected = selectedReportSections.includes(section.id);
                return <button key={section.id} onClick={() => toggleSection(section.id)} className={`flex items-center gap-2 px-2.5 py-1.5 rounded text-[11px] font-medium border transition-all text-left ${isSelected ? 'bg-astro-green/10 border-astro-green/30 text-astro-green' : 'bg-transparent border-astro-border text-astro-text-secondary hover:text-white hover:border-white/20'}`} data-id="element-262">
                          <div className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center shrink-0 ${isSelected ? 'bg-astro-green border-astro-green' : 'border-astro-border'}`} data-id="element-263">
                            {isSelected && <Check size={8} className="text-black" data-id="element-264" />}
                          </div>
                          {section.label}
                        </button>;
              })}
                  </div>

                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2" data-id="element-265">
                    <span className="text-[10px] text-astro-text-secondary" data-id="element-266">
                      {selectedReportSections.length} of {reportSections.length}{' '}
                      sections selected
                    </span>
                    <div className="flex items-center gap-2" data-id="element-267">
                      {reportReady && <button onClick={() => {
                  if (onViewReport && priorityClient) {
                    onViewReport(priorityClient, selectedReportSections);
                  }
                }} className="text-xs font-bold text-astro-green bg-astro-green/10 border border-astro-green/30 px-3 py-1.5 rounded transition-colors flex items-center gap-1.5 hover:bg-astro-green/20" data-id="element-268">
                          <FileText size={12} data-id="element-269" />
                          View Report
                        </button>}
                      <button onClick={handleGenerateReport} disabled={reportGenerating || selectedReportSections.length === 0} className="text-xs font-bold text-black bg-astro-green hover:bg-astro-green/90 px-3 py-1.5 rounded transition-colors flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed shadow-[0_0_10px_rgba(34,197,94,0.2)]" data-id="element-270">
                        {reportGenerating ? <>
                            <Loader2 size={12} className="animate-spin" data-id="element-271" />
                            Generating…
                          </> : reportReady ? <>
                            <Check size={12} data-id="element-272" />
                            Regenerate
                          </> : <>
                            <Sparkles size={12} data-id="element-273" />
                            Generate Report
                          </>}
                      </button>
                    </div>
                  </div>

                  {reportReady && <div className="mt-3 pt-3 border-t border-astro-border" data-id="element-274">
                      <div className="flex items-center gap-2 mb-2" data-id="element-275">
                        <CheckCircle2 size={12} className="text-astro-green" data-id="element-276" />
                        <span className="text-[11px] font-medium text-astro-green" data-id="element-277">
                          Report ready
                        </span>
                      </div>
                      <p className="text-[11px] text-astro-text-secondary leading-relaxed" data-id="element-278">
                        Remediation report for{' '}
                        <span className="text-white" data-id="element-279">
                          {priorityClient.clientName}
                        </span>{' '}
                        generated with {selectedReportSections.length} sections.
                        Includes {priorityClient.overPrivileged} over-privileged
                        identity findings and step-by-step remediation guidance.
                      </p>
                    </div>}
                </div>
              </div>}
          </div>}

        {/* AI Remediation Assistant */}
        {priorityClient && showRemediationAssistant && <div className="flex flex-col gap-4" data-id="element-280">
            <div className="bg-[#0a0e14] border border-astro-green/20 rounded-lg p-4" data-id="element-281">
              <div className="flex items-center justify-between mb-3" data-id="element-282">
                <div className="flex items-center gap-2.5" data-id="element-283">
                  <div className="w-7 h-7 rounded-lg bg-astro-green/15 flex items-center justify-center" data-id="element-284">
                    <Bot size={16} className="text-astro-green" data-id="element-285" />
                  </div>
                  <div data-id="element-286">
                    <h4 className="text-sm font-bold text-white" data-id="element-287">
                      Remediation Assistant
                    </h4>
                    <p className="text-[10px] text-astro-text-secondary" data-id="element-288">
                      {priorityClient.clientName} · {activeFindings.length}{' '}
                      findings
                    </p>
                  </div>
                </div>
                <button onClick={() => setShowRemediationAssistant(false)} className="text-astro-text-secondary hover:text-white transition-colors p-1" data-id="element-289">
                  <X size={14} data-id="element-290" />
                </button>
              </div>

              <div className="bg-astro-card border border-astro-border rounded-md p-3" data-id="element-291">
                <p className="text-xs text-astro-text-secondary leading-relaxed" data-id="element-292">
                  <span className="text-astro-green font-medium" data-id="element-293">
                    Analysis complete.
                  </span>{' '}
                  I found{' '}
                  <span className="text-white font-bold" data-id="element-294">
                    {activeFindings.length} over-permissioned identities
                  </span>{' '}
                  in {priorityClient.clientName}'s environment.{' '}
                  {highCount > 0 && <>
                      <span className="text-astro-danger font-bold" data-id="element-295">
                        {highCount} high severity
                      </span>{' '}
                      findings require immediate action.{' '}
                    </>}
                  Below are my recommendations with suggested policy changes for
                  each.
                </p>
              </div>

              <div className="flex items-center gap-2 mt-3" data-id="element-296">
                {highCount > 0 && <span className="flex items-center gap-1.5 px-2 py-1 rounded text-[10px] font-bold bg-astro-danger/10 text-astro-danger border border-astro-danger/20" data-id="element-297">
                    <span className="w-1.5 h-1.5 rounded-full bg-astro-danger" data-id="element-298"></span>
                    {highCount} High
                  </span>}
                {mediumCount > 0 && <span className="flex items-center gap-1.5 px-2 py-1 rounded text-[10px] font-bold bg-white/5 text-astro-text-secondary border border-white/10" data-id="element-299">
                    <span className="w-1.5 h-1.5 rounded-full bg-astro-text-secondary" data-id="element-300"></span>
                    {mediumCount} Medium
                  </span>}
                <span className="text-[10px] text-astro-text-secondary ml-auto" data-id="element-301">
                  {dismissedFindings.length} resolved
                </span>
              </div>
            </div>

            <div className="space-y-3" data-id="element-302">
              {activeFindings.map(finding => {
            const isExpanded = expandedFinding === finding.id;
            return <div key={finding.id} className="bg-[#0a0e14] border border-astro-border rounded-lg overflow-hidden transition-all" data-id="element-303">
                    <button onClick={() => setExpandedFinding(isExpanded ? null : finding.id)} className="w-full p-3.5 flex items-center justify-between text-left hover:bg-white/[0.02] transition-colors" data-id="element-304">
                      <div className="flex items-center gap-3 min-w-0" data-id="element-305">
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border shrink-0 ${finding.severity === 'HIGH' ? 'bg-astro-danger/10 text-astro-danger border-astro-danger/20' : 'bg-white/5 text-astro-text-secondary border-white/10'}`} data-id="element-306">
                          {finding.severity}
                        </span>
                        <div className="min-w-0" data-id="element-307">
                          <div className="flex items-center gap-2" data-id="element-308">
                            <span className="text-[10px] font-mono text-astro-green" data-id="element-309">
                              {finding.id}
                            </span>
                            <span className="text-xs font-bold text-white truncate" data-id="element-310">
                              {finding.title}
                            </span>
                          </div>
                          <span className="text-[10px] text-astro-text-secondary" data-id="element-311">
                            {finding.resourceType} · {finding.resourceName}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-2" data-id="element-312">
                        <span className="text-[10px] text-astro-text-secondary bg-white/5 px-1.5 py-0.5 rounded border border-white/10" data-id="element-313">
                          {finding.affected} affected
                        </span>
                        {isExpanded ? <ChevronUp size={14} className="text-astro-text-secondary" data-id="element-314" /> : <ChevronDown size={14} className="text-astro-text-secondary" data-id="element-315" />}
                      </div>
                    </button>

                    {isExpanded && <div className="px-3.5 pb-3.5 space-y-3 border-t border-astro-border pt-3" data-id="element-316">
                        <div data-id="element-317">
                          <span className="text-[9px] font-bold text-astro-text-secondary uppercase tracking-wider" data-id="element-318">
                            Description
                          </span>
                          <p className="text-xs text-astro-text-secondary/90 leading-relaxed mt-1" data-id="element-319">
                            {finding.description}
                          </p>
                        </div>

                        <div data-id="element-320">
                          <span className="text-[9px] font-bold text-astro-text-secondary uppercase tracking-wider" data-id="element-321">
                            Affected Resource
                          </span>
                          <div className="flex items-center gap-2 mt-1 bg-astro-card border border-astro-border rounded px-3 py-2" data-id="element-322">
                            <code className="text-[11px] font-mono text-astro-text-secondary flex-1 truncate" data-id="element-323">
                              {finding.affectedResource}
                            </code>
                            <button onClick={e => {
                      e.stopPropagation();
                      handleCopyResource(finding.affectedResource, finding.id);
                    }} className="text-astro-text-secondary hover:text-white transition-colors shrink-0" title="Copy" data-id="element-324">
                              {copiedResource === finding.id ? <Check size={13} className="text-astro-green" data-id="element-325" /> : <Copy size={13} data-id="element-326" />}
                            </button>
                          </div>
                        </div>

                        <div data-id="element-327">
                          <div className="flex items-center gap-1.5 mb-1" data-id="element-328">
                            <Sparkles size={10} className="text-astro-green" data-id="element-329" />
                            <span className="text-[9px] font-bold text-astro-green uppercase tracking-wider" data-id="element-330">
                              AI Recommendation
                            </span>
                          </div>
                          <p className="text-xs text-white/90 leading-relaxed" data-id="element-331">
                            {finding.recommendation}
                          </p>
                        </div>

                        {finding.suggestedPolicy && <div data-id="element-332">
                            <div className="flex items-center gap-1.5 mb-1" data-id="element-333">
                              <Shield size={10} className="text-astro-green" data-id="element-334" />
                              <span className="text-[9px] font-bold text-astro-green uppercase tracking-wider" data-id="element-335">
                                Suggested Policy
                              </span>
                            </div>
                            <pre className="text-[10px] font-mono text-astro-text-secondary bg-astro-card border border-astro-border rounded p-3 overflow-x-auto leading-relaxed" data-id="element-336">
                              {finding.suggestedPolicy}
                            </pre>
                          </div>}

                        <div className="flex flex-wrap items-center gap-2 pt-1" data-id="element-337">
                          <button onClick={() => setDismissedFindings(prev => [...prev, finding.id])} className="text-[11px] font-bold text-black bg-astro-green hover:bg-astro-green/90 px-3 py-1.5 rounded transition-colors flex items-center gap-1.5 shadow-[0_0_10px_rgba(34,197,94,0.15)]" data-id="element-338">
                            <ClipboardList size={11} data-id="element-339" />
                            Create Task
                          </button>
                          <button onClick={() => setDismissedFindings(prev => [...prev, finding.id])} className="text-[11px] font-medium text-white bg-white/5 border border-white/10 px-3 py-1.5 rounded hover:bg-white/10 transition-colors flex items-center gap-1.5" data-id="element-340">
                            <BookmarkX size={11} data-id="element-341" />
                            Mark as Accepted Risk
                          </button>
                          <button onClick={() => setDismissedFindings(prev => [...prev, finding.id])} className="text-[11px] font-medium text-astro-text-secondary border border-astro-border px-3 py-1.5 rounded hover:text-white hover:border-white/20 transition-colors flex items-center gap-1.5" data-id="element-342">
                            <EyeOff size={11} data-id="element-343" />
                            Suppress
                          </button>
                        </div>
                      </div>}
                  </div>;
          })}
            </div>

            {activeFindings.length === 0 && <div className="bg-[#0a0e14] border border-astro-green/20 rounded-lg p-6 text-center" data-id="element-344">
                <CheckCircle2 size={24} className="text-astro-green mx-auto mb-2" data-id="element-345" />

                <h4 className="text-sm font-bold text-white mb-1" data-id="element-346">
                  All findings addressed
                </h4>
                <p className="text-[11px] text-astro-text-secondary" data-id="element-347">
                  {dismissedFindings.length} findings have been triaged for{' '}
                  {priorityClient.clientName}.
                </p>
                <button onClick={() => setShowRemediationAssistant(false)} className="text-xs font-bold text-astro-green mt-3 hover:text-astro-green-dark transition-colors" data-id="element-348">
                  Return to overview
                </button>
              </div>}

            {activeFindings.length > 0 && <button onClick={() => setShowRemediationAssistant(false)} className="text-xs text-astro-text-secondary hover:text-white transition-colors self-start flex items-center gap-1" data-id="element-349">
                ← Back to overview
              </button>}
          </div>}
      </div>
    </div>;
}